import { SdkContext, toLegacyError } from '../internal'
import { listItems } from '@/lib/utils'
import type { RoomPanelDevice } from '@/types/roomPanel'
import type {
  Availability,
  AvailabilityException,
  BookableResource,
  Booking,
  BookingInput,
  BookingsPage,
  BookingsParams,
  ExceptionInput,
  PriceRule,
  PriceRuleInput,
  RescheduleInput,
  ResourceInput,
  ResourceKind,
  SearchParams,
  SearchResponse
} from '@/types/bookings'

const toPage = <T>(result: unknown): BookingsPage<T> => {
  const page = result as { pagination?: BookingsPage<T>['pagination'] } | null
  return { items: listItems<T>(result), pagination: page?.pagination ?? null }
}

/** Drops the keys that are `undefined`, so an unset filter never travels as the text "undefined". */
const clean = (query: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== ''))

/**
 * F-79: bookings (rooms, desks, stations). The same endpoints serve the Agendamento module
 * (`scheduling`): the space decides which one a call is about. Paths are written by hand (F-03).
 */
export class BookingsResource {
  constructor(private ctx: SdkContext) {}

  async amenities(): Promise<string[]> {
    return this.ctx.call({ httpMethod: 'GET', path: '/bookings/amenities' })
  }

  async listResources(params: { spaceId: string; kind?: ResourceKind; hostMemberId?: string; page?: number; limit?: number }): Promise<BookingsPage<BookableResource>> {
    return toPage(await this.ctx.raw({ httpMethod: 'GET', path: '/bookings/resources' }, { query: clean(params) }))
  }

  async getResource(resourceId: string): Promise<BookableResource> {
    return this.ctx.call({ httpMethod: 'GET', path: '/bookings/resources/{resourceId}' }, { resourceId })
  }

  async createResource(body: ResourceInput): Promise<BookableResource> {
    return this.ctx.call({ httpMethod: 'POST', path: '/bookings/resources', hasBody: true }, { body })
  }

  async updateResource(resourceId: string, body: ResourceInput): Promise<BookableResource> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/bookings/resources/{resourceId}', hasBody: true }, { resourceId, body })
  }

  /** 409 with `data.bookings` when there are future bookings; `cancelBookings` cancels them all and deletes. */
  async deleteResource(resourceId: string, cancelBookings = false): Promise<{ id: string; canceledBookings: number }> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/bookings/resources/{resourceId}' },
      { resourceId, query: cancelBookings ? { cancelBookings: true } : {} }
    )
  }

  async listExceptions(resourceId: string): Promise<AvailabilityException[]> {
    return listItems<AvailabilityException>(await this.ctx.raw({ httpMethod: 'GET', path: '/bookings/resources/{resourceId}/exceptions' }, { resourceId }))
  }

  /** `CLOSED` over active bookings answers 409 with `data.bookings`; `cancelBookings` cancels and refunds them. */
  async createException(resourceId: string, body: ExceptionInput): Promise<AvailabilityException> {
    return this.ctx.call({ httpMethod: 'POST', path: '/bookings/resources/{resourceId}/exceptions', hasBody: true }, { resourceId, body })
  }

  async deleteException(resourceId: string, exceptionId: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/bookings/resources/{resourceId}/exceptions/{exceptionId}' }, { resourceId, exceptionId })
  }

  async listPriceRules(resourceId: string): Promise<PriceRule[]> {
    return listItems<PriceRule>(await this.ctx.raw({ httpMethod: 'GET', path: '/bookings/resources/{resourceId}/price-rules' }, { resourceId }))
  }

  async createPriceRule(resourceId: string, body: PriceRuleInput): Promise<PriceRule> {
    return this.ctx.call({ httpMethod: 'POST', path: '/bookings/resources/{resourceId}/price-rules', hasBody: true }, { resourceId, body })
  }

  async updatePriceRule(resourceId: string, ruleId: string, body: PriceRuleInput): Promise<PriceRule> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/bookings/resources/{resourceId}/price-rules/{ruleId}', hasBody: true }, { resourceId, ruleId, body })
  }

  async deletePriceRule(resourceId: string, ruleId: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/bookings/resources/{resourceId}/price-rules/{ruleId}' }, { resourceId, ruleId })
  }

  async availability(params: { spaceId: string; date?: string; days?: number; resourceId?: string; hostMemberId?: string; durationMinutes?: number }): Promise<Availability> {
    return this.ctx.call({ httpMethod: 'GET', path: '/bookings/availability' }, { query: clean(params) })
  }

  async search(params: SearchParams): Promise<SearchResponse> {
    const { amenities, ...rest } = params
    return this.ctx.call(
      { httpMethod: 'GET', path: '/bookings/search' },
      { query: clean({ ...rest, amenities: amenities?.length ? amenities.join(',') : undefined }) }
    )
  }

  /** 409 carries `data.nextSlots` (taken) or `data.required/balance/shortfall` (not enough credits). */
  async create(body: BookingInput): Promise<Booking> {
    return this.ctx.call({ httpMethod: 'POST', path: '/bookings', hasBody: true }, { body })
  }

  async list(params: BookingsParams): Promise<BookingsPage<Booking>> {
    return toPage(await this.ctx.raw({ httpMethod: 'GET', path: '/bookings' }, { query: clean(params as Record<string, unknown>) }))
  }

  async get(bookingId: string): Promise<Booking> {
    return this.ctx.call({ httpMethod: 'GET', path: '/bookings/{bookingId}' }, { bookingId })
  }

  async cancel(bookingId: string, reason?: string): Promise<Booking> {
    return this.ctx.call({ httpMethod: 'POST', path: '/bookings/{bookingId}/cancel', hasBody: true }, { bookingId, body: reason ? { reason } : {} })
  }

  async reschedule(bookingId: string, body: RescheduleInput): Promise<Booking> {
    return this.ctx.call({ httpMethod: 'POST', path: '/bookings/{bookingId}/reschedule', hasBody: true }, { bookingId, body })
  }

  async noShow(bookingId: string): Promise<Booking> {
    return this.ctx.call({ httpMethod: 'POST', path: '/bookings/{bookingId}/no-show', hasBody: true }, { bookingId, body: {} })
  }

  /** Door panels of a room (F-79 1C). The team reads; owner, admin and moderator create and revoke. Never carries the token. */
  async listPanels(resourceId: string): Promise<RoomPanelDevice[]> {
    const data = await this.ctx.call<{ panels: RoomPanelDevice[] }>({ httpMethod: 'GET', path: '/bookings/resources/{resourceId}/panels' }, { resourceId })
    return data?.panels ?? []
  }

  /** The `url` (with the secret token in the fragment) comes back this once and is never readable again. */
  async createPanel(resourceId: string, name: string): Promise<{ panel: RoomPanelDevice; url: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/bookings/resources/{resourceId}/panels', hasBody: true }, { resourceId, body: { name } })
  }

  async revokePanel(resourceId: string, panelId: string): Promise<{ panel: RoomPanelDevice }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/bookings/resources/{resourceId}/panels/{panelId}' }, { resourceId, panelId })
  }

  /** "The meeting started", from the app: the booking's owner or the team. 409 outside the window. */
  async checkIn(bookingId: string): Promise<{ bookingId: string; checkedInAt: string; alreadyCheckedIn: boolean }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/bookings/{bookingId}/check-in', hasBody: true }, { bookingId, body: {} })
  }

  /**
   * The calendar invite as a file. Not through `client.request`: the transport reads every non-JSON
   * body as text. Goes straight to fetch with the headers the client would send.
   */
  async calendar(bookingId: string): Promise<Blob> {
    const client = await this.ctx.ready
    const state = client.config
    const headers: Record<string, string> = { Accept: 'text/calendar' }
    if (state.token) headers.Authorization = 'Bearer ' + state.token
    if (state.communityId) headers['X-CommunityId'] = state.communityId
    if (state.memberId) headers['X-MemberId'] = state.memberId
    if (state.language) headers['Accept-Language'] = state.language
    const base = String(state.baseUrl || '').replace(/\/+$/, '')
    const response = await fetch(`${base}/api/bookings/${encodeURIComponent(bookingId)}/calendar.ics`, { headers })
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      throw toLegacyError({ name: 'MemberfyError', message: payload?.message || response.statusText, status: response.status, body: payload })
    }
    return response.blob()
  }
}
